/**
 * Logger Utility
 *
 * Centralized logging using loglevel library.
 * Automatically configures log level based on environment.
 *
 * Usage:
 * ```typescript
 * import { logger } from '@/utils/logger';
 *
 * logger.debug('Debug message');
 * logger.info('Info message');
 * logger.warn('Warning message');
 * logger.error('Error message');
 * ```
 *
 * Log Levels (in order of severity):
 * - TRACE (0): Very detailed debugging
 * - DEBUG (1): Detailed debugging
 * - INFO (2): General information
 * - WARN (3): Warnings
 * - ERROR (4): Errors
 * - SILENT (5): No logging
 *
 * Environment Configuration:
 * - Development: DEBUG level (shows debug, info, warn, error)
 * - Production: WARN level (shows only warn, error)
 * - Can be overridden with LOG_LEVEL environment variable
 */

import log from "loglevel";

// Configure default log level based on environment
const isDevelopment = import.meta.env.MODE === "development";
const isTest = import.meta.env.MODE === "test";

// Default levels:
// - Development: DEBUG (shows debug, info, warn, error)
// - Test: WARN (shows only warn, error during tests)
// - Production: WARN (shows only warn, error)
const defaultLevel: log.LogLevelDesc = isDevelopment ? "debug" : "warn";

// Allow override via environment variable
const envLevel = import.meta.env.VITE_LOG_LEVEL as log.LogLevelDesc | undefined;
const logLevel = envLevel || defaultLevel;

// Set the log level
log.setLevel(logLevel);

// During tests, suppress all logs unless explicitly enabled
if (isTest && !envLevel) {
  log.setLevel("silent");
}

/**
 * Application logger
 *
 * @example
 * // Debug logging (only in development)
 * logger.debug('User data loaded:', userData);
 *
 * @example
 * // Info logging
 * logger.info('Authentication successful');
 *
 * @example
 * // Warning logging
 * logger.warn('API returned unexpected format');
 *
 * @example
 * // Error logging
 * logger.error('Failed to fetch user data:', error);
 */
export const logger = log;

/**
 * Create a namespaced logger for a specific module
 *
 * @param name - Module or component name
 * @returns Logger instance with prefixed name
 *
 * @example
 * const log = createLogger('AuthHook');
 * log.debug('Fetching user data...');
 * // Output: [AuthHook] Fetching user data...
 */
export function createLogger(name: string): log.Logger {
  return log.getLogger(name);
}

// Export convenience methods for direct use
export const { trace, debug, info, warn, error } = logger;

export default logger;
